import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod/v4';
import { VizContainerVizErrorBoundary } from '@/components/VizContainer/viz-error-boundary/VizContainerVizErrorBoundary';
import { validateSchema } from '@/dashboards/utils/schema';
import { JSONValue } from '../../core';
import { ResponsivePanelWrapper } from '../components/ResponsivePanelWrapper';
import { VizPanelProps } from '../components/VizPanel';
import { VizPanelWrapper } from '../components/VizPanelWrapper';
import {
  BasePanelProps,
  PanelComponentProps,
  PanelPlugin,
  useVizFrameworkConfig,
  VizBaseComponentProps,
  VizComponentProps,
} from '../model';

// TODO: do refactor that enables us to pick these values from ChartProps
export type CommonChartProps =
  | 'width'
  | 'height'
  | 'panelKey'
  | 'links'
  | 'onPluginSpecChange'
  | 'panelChromeProps'
  | 'layoutStrategy';

/**
 * Configuration options that determine how a visualization is prepared for
 * rendering.
 */
export interface CreateForVizOptions extends Pick<VizPanelProps, 'noPadding'> {}

/**
 * Configuration options used to create a simple visualization that does not
 * currently support dashboards.
 *
 * Useful for early development.
 */
export interface VizOnlyCreateOptions<ChildComponents extends object> {
  vizConfig?: CreateForVizOptions;
  /**
   * Mapping of child components to be exposed to consumers for more complex composition of panels when using the `Viz`
   * variation of panel plugins.
   *
   * Names of child components should be PascalCase and avoid using reserved words for this feature (Viz,
   * DashboardPanel). When adding a new child component, please check out existing use cases to see if there is already
   * a common name to use (e.g. `PanelContent` is used for the view layer for rendering the panel).
   */
  childComponents?: ChildComponents;
}

/**
 * Configuration options that determine how a visualization is prepared for
 * rendering inside a dashboard.
 */
export interface CreateForDashboardOptions<ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>
  extends Omit<PanelPlugin<DashboardSpec>, 'PanelComponent'> {
  kind: string;
  /**
   * Function that translates the dashboard spec for the plugin to the component
   * props for the plugin. This function is critical for ensuring plugins work
   * with native dashboards.
   */
  mapSpecToProps: (spec: DashboardSpec) => Omit<ComponentProps, CommonChartProps>;
  /**
   * Function that translates the the component props for the plugin to the dashboard
   * spec for the plugin. This function is critical for enabling the "add to dashboard"
   * feature.
   */
  mapPropsToSpec: (props: Omit<ComponentProps, CommonChartProps>) => DashboardSpec;
}

/**
 * Configuration options used to create a visualization that supports both
 * dashboard and non-dashboard use cases.
 *
 * Most visualizations should evolve to use these options over time.
 */
export interface CombinedCreateOptions<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object,
> {
  dashboardConfig: CreateForDashboardOptions<ComponentProps, DashboardSpec>;
  vizConfig?: CreateForVizOptions;
  /**
   * Mapping of child components to be exposed to consumers for more complex composition of panels when using the `Viz`
   * variation of panel plugins.
   *
   * Names of child components should be PascalCase and avoid using reserved words for this feature (Viz,
   * DashboardPanel). When adding a new child component, please check out existing use cases to see if there is already
   * a common name to use (e.g. `PanelContent` is used for the view layer for rendering the panel).
   */
  childComponents?: ChildComponents;
}

export type VizOnlyVisualization<
  ComponentProps extends BasePanelProps,
  ChildComponents extends object = object,
> = ChildComponents & {
  /**
   * Visualization for rendering outside a dashboard.
   *
   * This version of the visualization is automatically wrapped in a `VizPanel`, which can be configured using
   * the `panelChromeProps` prop. If you want more control over composition of the layout, instead use the `.VizBase`
   * version of the component with `VizContainer`.
   */
  Viz: React.ComponentType<VizComponentProps<ComponentProps>>;
  /**
   * Visualization for rendering outside a dashboard using composition. Should be rendered inside a `VizContainer`.
   *
   * This version of the visualization only provides the visualization and must be composed inside of a `VizContainer`.
   * If you want a visualization that is automatically wrapped in a `VizPanel`, instead use the `.Viz` version of the
   * component.
   */
  VizBase: React.ComponentType<VizBaseComponentProps<ComponentProps>>;
};

export type CombinedVisualization<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object = object,
> = ChildComponents & {
  /**
   * Visualization for rendering outside a dashboard.
   *
   * This version of the visualization is automatically wrapped in a `VizPanel`, which can be configured using
   * the `panelChromeProps` prop. If you want more control over composition of the layout, instead use the `.VizBase`
   * version of the component with `VizContainer`.
   */
  Viz: React.ComponentType<VizComponentProps<ComponentProps>>;

  /**
   * Visualization for rendering outside a dashboard using composition. Should be rendered inside a `VizContainer`.
   *
   * This version of the visualization only provides the visualization and must be composed inside of a `VizContainer`.
   * If you want a visualization that is automatically wrapped in a `VizPanel`, instead use the `.Viz` version of the
   * component.
   */
  VizBase: React.ComponentType<VizBaseComponentProps<ComponentProps>>;

  /**
   * Visualization for rendering inside a dashboard.
   */
  DashboardPanel: PanelPlugin<DashboardSpec, ComponentProps> & CreateForDashboardOptions<ComponentProps, DashboardSpec>;
};

// Type signature for including both "viz" and "dashboard."
// It's important that this overloaded type signature comes before the "viz" only version because otherwise
// the types can sometimes get set incorrectly in some edge cases.
export function createVisualization<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object = object,
>(
  Component: React.ComponentType<ComponentProps>,
  config: CombinedCreateOptions<ComponentProps, DashboardSpec, ChildComponents>
): CombinedVisualization<ComponentProps, DashboardSpec, ChildComponents>;

// Type signature for just "viz" version. Useful for early development before ready for dashboard features.
export function createVisualization<ComponentProps extends BasePanelProps, ChildComponents extends object = object>(
  Component: React.ComponentType<ComponentProps>,
  config?: VizOnlyCreateOptions<ChildComponents>
): VizOnlyVisualization<ComponentProps, ChildComponents>;

/**
 * Sets up the visualization to work for one or more of the use cases supported
 * by the framework:
 * - `Viz` (required): Visualization for rendering outside a dashboard.
 * - `DashboardPanel` (optional): Visualization for rendering inside a dashboard.
 */
export function createVisualization<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object = object,
>(
  Component: React.ComponentType<ComponentProps>,
  config?: CombinedCreateOptions<ComponentProps, DashboardSpec, ChildComponents> | VizOnlyCreateOptions<ChildComponents>
) {
  const Viz = createForViz(Component, config);
  const VizBase = createForVizBase(Component);

  // Support all use cases (dashboard and not-dashboard).
  if (config && 'dashboardConfig' in config) {
    return {
      ...config?.childComponents,
      DashboardPanel: createForDashboard(Component, config),
      Viz,
      VizBase,
    };
  }

  // Only support the not-dashboard use case.
  return {
    ...config?.childComponents,
    Viz,
    VizBase,
  };
}

/**
 * Prepares the visualization for rendering inside a dashboard.
 *
 * Used in dashboard-driven product features (currently only native dashboards).
 *
 * Under the hood this returns a panel plugin with the properties necessary for
 * viewing and managing the visualization within a dashboard.
 */
function createForDashboard<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object,
>(
  Component: React.ComponentType<ComponentProps>,
  { dashboardConfig, vizConfig }: CombinedCreateOptions<ComponentProps, DashboardSpec, ChildComponents>
) {
  const ComponentWithVizPanel = withVizPanel(Component, {
    dashboardConfig,
    vizConfig,
  });

  const PanelComponent = ({
    spec,
    panelKey,
    links,
    onPluginSpecChange,
    panelChromeProps,
    width,
    height,
    ...rest
  }: PanelComponentProps<DashboardSpec, ComponentProps>) => {
    const nextProps = useMemo(() => {
      return dashboardConfig.mapSpecToProps(spec);
    }, [spec]);

    const validationError = useValidatePanelPluginSchema({
      pluginSchema: dashboardConfig.schema,
      kind: dashboardConfig.kind,
      spec,
    });

    return (
      <ComponentWithVizPanel
        {...(nextProps as ComponentProps)}
        {...rest}
        panelKey={panelKey}
        links={links}
        onPluginSpecChange={onPluginSpecChange}
        panelChromeProps={{ ...panelChromeProps, validationError }}
        width={width}
        height={height}
      />
    );
  };

  const plugin: PanelPlugin<DashboardSpec, ComponentProps> & CreateForDashboardOptions<ComponentProps, DashboardSpec> =
    {
      ...dashboardConfig,
      PanelComponent: PanelComponent,
    };
  return plugin;
}

/**
 * Prepares the visualization for rendering outside a dashboard.
 *
 * Used in product features that are not driven by dashboards (most use cases).
 *
 * Under the hood, this returns the visualization component wrapped in a
 * higher-order-component that adds `VizPanel` and other useful functionality
 * for context-aware visualizations.
 */
function createForViz<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object,
>(
  Component: React.ComponentType<ComponentProps>,
  config:
    | CombinedCreateOptions<ComponentProps, DashboardSpec, ChildComponents>
    | VizOnlyCreateOptions<ChildComponents> = {}
): React.ComponentType<VizComponentProps<ComponentProps>> {
  return withVizPanel(Component, config);
}

/**
 * Wraps the specified component in `VizPanelWrapper`.
 */
function withVizPanel<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
  ChildComponents extends object,
>(
  Component: React.ComponentType<ComponentProps>,
  config?: CombinedCreateOptions<ComponentProps, DashboardSpec, ChildComponents> | VizOnlyCreateOptions<ChildComponents>
) {
  const HOCComponent = (props: VizComponentProps<ComponentProps>) => {
    const { width, height, panelChromeProps, ...rest } = props;

    return (
      <VizPanelWrapper
        chartKind={config && 'dashboardConfig' in config ? config.dashboardConfig.kind : undefined}
        chartProps={rest as VizComponentProps<ComponentProps>}
        width={width}
        height={height}
        mapPropsToSpec={config && 'dashboardConfig' in config ? config.dashboardConfig.mapPropsToSpec : undefined}
        panelChromeProps={{ noPadding: config?.vizConfig?.noPadding, ...panelChromeProps }}
      >
        <Component
          {...(rest as ComponentProps)}
          // Pass along the layout strategy to support occasional use cases where the lower level chart needs this
          // information. We avoid passing the entire `panelChromeProps` to avoid confusion for the `.VizBase` use
          // cases.
          layoutStrategy={panelChromeProps?.layoutStrategy}
        />
      </VizPanelWrapper>
    );
  };
  HOCComponent.displayName = `withVizPanel(${Component.displayName})`;
  return HOCComponent;
}

/**
 * Prepares the visualization for rendering outside a dashboard as part of a composition pattern using `VizContainer`.
 *
 * Used in product features that are not driven by dashboards (most use cases).
 */
function createForVizBase<ComponentProps extends BasePanelProps>(
  Component: React.ComponentType<ComponentProps>
): React.ComponentType<VizBaseComponentProps<ComponentProps>> {
  const HOCComponent = (props: VizBaseComponentProps<ComponentProps>) => {
    const {
      width,
      height,
      // Default to `flex-height` in `.VizBase` use cases because that is likely to be what consumers want in these
      // use cases.
      layoutStrategy = 'flex-height',
      ErrorBoundaryComponent = VizContainerVizErrorBoundary,
      errorBoundaryProps,
      ...rest
    } = props;

    return (
      <ErrorBoundaryComponent {...errorBoundaryProps}>
        <ResponsivePanelWrapper width={width} height={height}>
          <Component layoutStrategy={layoutStrategy} {...(rest as ComponentProps)} />
        </ResponsivePanelWrapper>
      </ErrorBoundaryComponent>
    );
  };
  HOCComponent.displayName = `withVizBase(${Component.displayName})`;
  return HOCComponent;
}

/**
 * Helper hook to validate the schema of a panel plugin.
 */
function useValidatePanelPluginSchema({
  pluginSchema,
  spec,
  kind,
}: {
  pluginSchema: z.ZodTypeAny | undefined;
  spec: unknown;
  kind: string;
}) {
  const config = useVizFrameworkConfig();
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    // Reset error whenever we re-process.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(undefined);

    const enableSchemaValidation = config?.enableSchemaValidation ?? false;
    if (pluginSchema && enableSchemaValidation) {
      const validationResults = validateSchema(pluginSchema, spec);
      if (validationResults.success === false) {
        setError(
          new Error(
            `Invalid schema for panel kind \`${kind}\`: ${validationResults.errors?.map((error) => error.message).join(', ')}`
          )
        );
      }
    }
  }, [spec, kind, pluginSchema, config?.enableSchemaValidation]);

  return error;
}
